import React, { useContext, useEffect, useReducer, useState } from "react";
import { Link as RouterLink, useHistory, useLocation } from "react-router-dom";
import isNativeApp from "../utils/isNativeApp";

import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import ListSubheader from "@material-ui/core/ListSubheader";
import Divider from "@material-ui/core/Divider";
import { Badge, Collapse, List, Avatar } from "@material-ui/core";
import { useActiveMenu } from "../context/ActiveMenuContext";

import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import PhoneCallbackIcon from "@material-ui/icons/PhoneCallback";
import SyncAltIcon from "@material-ui/icons/SyncAlt";
import SettingsOutlinedIcon from "@material-ui/icons/SettingsOutlined";
import PeopleAltOutlinedIcon from "@material-ui/icons/PeopleAltOutlined";
import ContactPhoneOutlinedIcon from "@material-ui/icons/ContactPhoneOutlined";
import AccountTreeOutlinedIcon from "@material-ui/icons/AccountTreeOutlined";
import FlashOnIcon from "@material-ui/icons/FlashOn";

import EventIcon from "@material-ui/icons/Event";
import EventAvailableIcon from "@material-ui/icons/EventAvailable";
import EmojiEventsIcon from '@material-ui/icons/EmojiEvents';

import PieChartIcon from '@material-ui/icons/PieChart';

import LocalOfferIcon from "@material-ui/icons/LocalOffer";

import ListIcon from "@material-ui/icons/ListAlt";
import AnnouncementIcon from "@material-ui/icons/Announcement";
import ForumIcon from "@material-ui/icons/Forum";
import CampaignIcon from "@material-ui/icons/Send";

import { useTranslation } from "react-i18next";
import { SidebarSearchContext } from "../context/SidebarSearch";
import BorderColorIcon from '@material-ui/icons/BorderColor';
import { WhatsAppsContext } from "../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../context/Auth/AuthContext";
import { useVoice } from "../context/Voice/VoiceContext";
import { Can } from "../components/Can";
import { SocketContext } from "../context/Socket/SocketContext";
import { isArray } from "lodash";
import api from "../services/api";
import toastError from "../errors/toastError";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Tooltip from "@material-ui/core/Tooltip";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import EmojiObjectsIcon from "@material-ui/icons/EmojiObjects";
import SmartToyIcon from "@material-ui/icons/Android";
import GroupIcon from "@material-ui/icons/Group";
import CardGiftcardIcon from "@material-ui/icons/CardGiftcard";
import SendIcon from "@material-ui/icons/Send";
import PaymentIcon from "@material-ui/icons/Payment";

import Typography from "@material-ui/core/Typography";

// Sidebar styling pulled toward Linear / Stripe / Vercel territory:
//   * tighter rows (36px) instead of 42px chunky list items
//   * icons render as plain SVG with a muted color, not as a heavy
//     gradient avatar pill — the chip only fills in on the active row
//   * active state is a subtle brand-green tinted bg + brand-green
//     icon + an accent bar on the inside edge (right in RTL, left in
//     LTR) — visual hierarchy without the bright gradient pill
//   * section subheaders sit in their own tighter band with an
//     optional top hairline so groupings read at a glance
//   * smooth ~140ms transitions, no transform — feels closer to
//     "selecting a file in a tree view" than "tapping a button"
const useStyles = makeStyles((theme) => {
  const isLight = theme.mode === "light" || theme.palette.type === "light";
  const isRtl = theme.direction === "rtl";

  return {
    // Halo pulse lives on a ::after pseudo-element and animates only
    // transform + opacity (compositor-friendly) — the old version
    // animated box-shadow on the badge itself, which forced a repaint
    // on every frame for as long as any chat was unread.
    "@keyframes chatBadgePulse": {
      "0%": {
        transform: "scale(1)",
        opacity: 0.7,
      },
      "70%, 100%": {
        transform: "scale(1.6)",
        opacity: 0,
      },
    },
    chatUnreadBadge: {
      position: "relative",
      zIndex: 0,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 20,
      height: 20,
      borderRadius: 10,
      padding: "0 5px",
      background: "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
      color: "#ffffff",
      fontSize: 11,
      fontWeight: 800,
      marginLeft: 8,
      marginRight: 4,
      boxShadow: "0 2px 8px rgba(37,211,102,0.55)",
      flexShrink: 0,
      "&::after": {
        content: '""',
        position: "absolute",
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        zIndex: -1,
        borderRadius: "inherit",
        background: "rgba(37,211,102,0.45)",
        animation: "$chatBadgePulse 1.6s ease-in-out infinite",
        pointerEvents: "none",
      },
      "@media (prefers-reduced-motion: reduce)": {
        "&::after": {
          animation: "none",
          opacity: 0,
        },
      },
    },
    listItemPrimaryRow: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      width: "100%",
    },
    ListSubheader: {
      height: 30,
      lineHeight: "30px",
      marginTop: theme.spacing(2),
      marginBottom: 4,
      paddingLeft: 24,
      paddingRight: 24,
      fontSize: 11,
      fontWeight: 800,
      letterSpacing: "0.9px",
      textTransform: "uppercase",
      color: isLight ? "#94a3b8" : "rgba(255,255,255,0.45)",
      backgroundColor: "transparent",
      // Tiny hairline above each section so groups read as
      // visually distinct blocks, not one continuous list.
      borderTop: isLight
        ? "1px solid rgba(15, 23, 42, 0.05)"
        : "1px solid rgba(255,255,255,0.05)",
      paddingTop: 10,
    },
    // Premium sidebar pattern from research (Linear, Vercel, Cursor):
    //   * inactive rows DIM via opacity (0.78), not via gray text
    //     swap — keeps palette coherent
    //   * hover bumps opacity to 1 + 3% white surface tint, no
    //     brand color in hover state
    //   * active row: subtle horizontal brand gradient bg
    //     (10% → 2%), brand-ink text, RTL-aware accent rail
    //     pinned to the inside edge. NO heavy fill on the icon —
    //     icon also becomes brand-color but stays "ghost", not a
    //     full gradient chip
    listItem: {
      position: "relative",
      height: 44,
      width: "auto",
      borderRadius: 10,
      margin: "2px 12px",
      padding: "0 12px",
      opacity: 0.82,
      transition: "opacity .15s ease, background-color .15s ease, color .15s ease",
      "& .MuiListItemIcon-root": {
        minWidth: 36,
      },
      "&:hover": {
        opacity: 1,
        backgroundColor: isLight
          ? "rgba(15, 23, 42, 0.04)"
          : "#131614",
      },
      "&:hover $iconHoverActive": {
        color: isLight ? "#0f172a" : "#fafafa",
        backgroundColor: "transparent",
      },
      "&:hover $listItemText": {
        color: isLight ? "#0f172a" : "#fafafa",
      },
      "&[data-active='true']": {
        opacity: 1,
        background: isRtl
          ? "linear-gradient(270deg, rgba(46, 227, 122, 0.10), rgba(46, 227, 122, 0.02))"
          : "linear-gradient(90deg, rgba(46, 227, 122, 0.10), rgba(46, 227, 122, 0.02))",
        "& $listItemText": {
          color: isLight ? "#075e54" : "#7df0a8",
          fontWeight: 700,
        },
        "& $iconHoverActive": {
          background: "transparent",
          color: isLight ? "#128c7e" : "#7df0a8",
          boxShadow: "none",
        },
        // Inside-edge accent rail (right in RTL, left in LTR).
        // 2px instead of 3px — the rail should be a hint, not a
        // bar. Solid color, no gradient at this scale.
        "&::before": {
          content: '""',
          position: "absolute",
          top: 8,
          bottom: 8,
          [isRtl ? "right" : "left"]: 0,
          width: 2,
          borderRadius: 2,
          background: isLight ? "#25d366" : "#2ee37a",
        },
      },
    },
    listItemText: {
      fontSize: 14,
      fontWeight: 600,
      lineHeight: 1.2,
      color: isLight ? "#475569" : "rgba(255,255,255,0.82)",
      transition: "color .15s ease",
    },
    avatarActive: { backgroundColor: "transparent" },
    avatarHover: { backgroundColor: "transparent" },
    // Icon chip — pastel rounded square that hosts the menu icon.
    // Brand-green tint on hover, full brand gradient when active.
    // 32x32 — visible enough to read at a glance, not so big it
    // dominates the row.
    iconHoverActive: {
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      borderRadius: 10,
      height: 32,
      width: 32,
      backgroundColor: isLight
        ? "rgba(15, 23, 42, 0.04)"
        : "rgba(255,255,255,0.05)",
      color: isLight ? "#475569" : "rgba(255,255,255,0.7)",
      transition: "all .15s ease",
      "& .MuiSvgIcon-root": {
        fontSize: "1.3rem",
      },
    },
    formField: {},
    divider: {
      margin: "10px 18px",
      backgroundColor: isLight
        ? "rgba(15, 23, 42, 0.06)"
        : "rgba(255,255,255,0.06)",
    },
  };
});


// Memoized so socket-driven chat reducer updates in MainListItems
// (a fresh array on every tick) don't re-run every nav row. All props
// are primitives except `icon`, which is a fresh JSX element each
// render — compare it structurally (see sameIconElement below).
const ListItemLink = React.memo(function ListItemLink(props) {
  const { icon, primary, to, className, tooltip, showBadge, dataTour, badgeCount } = props;
  const classes = useStyles();
  const { activeMenu } = useActiveMenu();
  const location = useLocation();
  const isActive = activeMenu === to || location.pathname === to;
  // Sidebar search filter — when the user types in the search input
  // at the top of the drawer, hide every row whose label doesn't
  // match (case-insensitive, substring). The active row is always
  // kept visible so the user never loses orientation.
  const sidebarSearch = useContext(SidebarSearchContext);

  const renderLink = React.useMemo(
    () =>
      React.forwardRef((itemProps, ref) => (
        <RouterLink to={to} ref={ref} {...itemProps} />
      )),
    [to]
  );

  // Hide AFTER all hooks have run, to satisfy rules-of-hooks. The
  // hook calls above are unconditional regardless of search state.
  if (sidebarSearch && !isActive) {
    const label = typeof primary === "string" ? primary : "";
    if (!label.toLowerCase().includes(sidebarSearch.toLowerCase())) {
      return null;
    }
  }

  const ConditionalTooltip = ({ children, tooltipEnabled }) =>
    tooltipEnabled ? (
      <Tooltip title={primary} placement="right">
        {children}
      </Tooltip>
    ) : (
      children
    );
    return (
      <ConditionalTooltip tooltipEnabled={!!tooltip}>
        <li data-tour={dataTour}>
          <ListItem
            button
            component={renderLink}
            className={classes.listItem}
            data-active={isActive ? "true" : "false"}
          >
            {icon ? (
              <ListItemIcon>
                {showBadge ? (
                  <Badge badgeContent="!" color="error" overlap="circular" className={classes.badge}>
                    <Avatar className={`${classes.iconHoverActive} ${isActive ? "active" : ""}`}>{icon}</Avatar>
                  </Badge>
                ) : (
                  <Avatar className={`${classes.iconHoverActive} ${isActive ? "active" : ""}`}>{icon}</Avatar>
                )}
              </ListItemIcon>
            ) : null}
            <ListItemText
              primary={
                <span className={classes.listItemPrimaryRow}>
                  <Typography className={classes.listItemText}>{primary}</Typography>
                  {badgeCount > 0 && (
                    <span className={classes.chatUnreadBadge}>
                      {badgeCount > 99 ? "99+" : badgeCount}
                    </span>
                  )}
                </span>
              }
            />
          </ListItem>
        </li>
      </ConditionalTooltip>
    );
}, (prevProps, nextProps) => {
  const keys = new Set([...Object.keys(prevProps), ...Object.keys(nextProps)]);
  for (const key of keys) {
    if (key === "icon") {
      if (!sameIconElement(prevProps.icon, nextProps.icon)) return false;
    } else if (!Object.is(prevProps[key], nextProps[key])) {
      return false;
    }
  }
  return true;
});

// Structural equality for the `icon` prop — JSX like <WhatsAppIcon />
// is a fresh element every render, so identity comparison would defeat
// the memo. Compare element type + props (recursing into children so
// e.g. the Connections <Badge badgeContent={...}> still re-renders
// when the warning flag flips).
function sameIconElement(a, b) {
  if (a === b) return true;
  if (!React.isValidElement(a) || !React.isValidElement(b)) return Object.is(a, b);
  if (a.type !== b.type) return false;
  const aKeys = Object.keys(a.props);
  const bKeys = Object.keys(b.props);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) =>
    key === "children"
      ? sameIconElement(a.props[key], b.props[key])
      : Object.is(a.props[key], b.props[key])
  );
}

const reducer = (state, action) => {
  if (action.type === "LOAD_CHATS") {
    const chats = action.payload;
    const newChats = [];

    if (isArray(chats)) {
      chats.forEach((chat) => {
        const chatIndex = state.findIndex((u) => u.id === chat.id);
        if (chatIndex !== -1) {
          state[chatIndex] = chat;
        } else {
          newChats.push(chat);
        }
      });
    }

    return [...state, ...newChats];
  }

  if (action.type === "UPDATE_CHATS") {
    const chat = action.payload;
    const chatIndex = state.findIndex((u) => u.id === chat.id);

    if (chatIndex !== -1) {
      state[chatIndex] = chat;
      return [...state];
    } else {
      return [chat, ...state];
    }
  }

  if (action.type === "DELETE_CHAT") {
    const chatId = action.payload;

    const chatIndex = state.findIndex((u) => u.id === chatId);
    if (chatIndex !== -1) {
      state.splice(chatIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }

  if (action.type === "CHANGE_CHAT") {
    const chat = action.payload?.chat;
    if (!chat) return state;
    const chatIndex = state.findIndex((u) => u.id === chat.id);
    if (chatIndex !== -1) {
      // Immutable update — spread ensures we don't mutate state directly
      return state.map((u) => (u.id === chat.id ? { ...u, ...chat } : u));
    } else {
      // Add the chat if it's not in the list yet (e.g. first message received)
      return [chat, ...state];
    }
  }
};

const MainListItems = (props) => {
  const classes = useStyles();
  const theme = useTheme();
  const isLightMode = theme.palette.type === "light"; // MUI v4: palette.type is the light/dark discriminator (theme.mode doesn't exist)
  const { drawerClose, drawerOpen, collapsed} = props;
  const { whatsApps } = useContext(WhatsAppsContext);
  const { user ,handleLogout} = useContext(AuthContext);
  const [connectionWarning, setConnectionWarning] = useState(false);
  const { t } = useTranslation();
  // Voice feature flag (company-level). The sidebar sits inside the hoisted
  // VoiceProvider, so the Voice menu links respect the flag like every other
  // Voice surface — hidden unless the company has Voice enabled.
  const voice = useVoice();
  const voiceOn = Boolean(voice && voice.status && voice.status.enabled);

  const history = useHistory();
  const [invisible, setInvisible] = useState(true);
  const [unreadsCount, setUnreadsCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam] = useState("");
  const [chats, dispatch] = useReducer(reducer, []);
  const [version, setVersion] = useState("v N/A");

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchChats();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParam, pageNumber]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.GetSocket(companyId);

    // General chat events — new messages, updates (carries data.chat)
    const onCompanyChat = (data) => {
      if (data.action === "new-message" || data.action === "update") {
        dispatch({ type: "CHANGE_CHAT", payload: { chat: data.chat } });
      }
    };

    // Per-user chat events — carries data.record (unreads, membership changes)
    const onChatUser = (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "CHANGE_CHAT", payload: { chat: data.record } });
      }
    };

    socket.on(`company-${companyId}-chat`, onCompanyChat);
    socket.on(`company-${companyId}-chat-user-${user.id}`, onChatUser);

    return () => {
      socket.off(`company-${companyId}-chat`, onCompanyChat);
      socket.off(`company-${companyId}-chat-user-${user.id}`, onChatUser);
    };
  }, [socketManager, user.id]);

  useEffect(() => {
    let unreadsCount = 0;
    if (chats.length > 0) {
      for (let chat of chats) {
        for (let chatUser of chat.users) {
          if (chatUser.userId === user.id) {
            unreadsCount += chatUser.unreads;
          }
        }
      }
    }
    if (unreadsCount > 0) {
      setInvisible(false);
      setUnreadsCount(unreadsCount);
    } else {
      setInvisible(true);
      setUnreadsCount(0);
    }
  }, [chats, user.id]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (whatsApps.length > 0) {
        const offlineWhats = whatsApps.filter((whats) => {
          return (
            whats.status === "qrcode" ||
            whats.status === "PAIRING" ||
            whats.status === "DISCONNECTED" ||
            whats.status === "TIMEOUT" ||
            whats.status === "OPENING"
          );
        });
        if (offlineWhats.length > 0) {
          setConnectionWarning(true);
        } else {
          setConnectionWarning(false);
        }
      }
    }, 2000);
    return () => clearTimeout(delayDebounceFn);
  }, [whatsApps]);

  const fetchChats = async () => {
    try {
      const { data } = await api.get("/chats/", {
        params: { searchParam, pageNumber },
      });
      dispatch({ type: "LOAD_CHATS", payload: data.records });
    } catch (err) {
      toastError(err);
    }
  };

  const handleClickLogout = () => {
    //handleCloseMenu();
    handleLogout();
  };

  return (
    <div onClick={drawerClose}>
      <Can
        role={user.profile}
        perform={"drawer-service-items:view"}
        style={{
          overflowY: "scroll",
        }}
        no={()=>(
        <>
          <ListSubheader 
          hidden={!drawerOpen}
        style={{
          position:"relative",
          fontSize: "14px",
          fontWeight: 600,
          textAlign: "right",
          padding: "5px 14px",
          margin: "8px 12px 4px 12px",
          color: isLightMode ? "#111827" : "#e8efd8",
          border: "1px solid rgba(46,227,122,0.35)",
          backgroundColor: "rgba(46,227,122,0.08)",
          borderRadius: "8px",
          boxShadow: "0 1px 4px rgba(46,227,122,0.10)",
          lineHeight: "1.5",
        }}   
        inset
        color="inherit">
              {t("mainDrawer.listItems.service")}
          </ListSubheader>
          <>
            
            <ListItemLink
              to="/tickets"
              primary={t("mainDrawer.listItems.tickets")}
              icon={<WhatsAppIcon />}
              tooltip={collapsed}
            />
            {/* Voice Intelligence folded into the AI-OS shell (One-Experience
                consolidation 22.07) — reachable from /ai-os quick access. */}
            <ListItemLink
              to="/appointments"
              primary={t("mainDrawer.listItems.appointments")}
              icon={<EventAvailableIcon />}
              tooltip={collapsed}
            />
            <ListItemLink
              to="/chats"
              primary={t("mainDrawer.listItems.chats")}
              icon={<ForumIcon />}
              badgeCount={invisible ? 0 : unreadsCount}
              tooltip={collapsed}
            />
      <ListItemLink
      className={classes.formField}

        to="/todolist"
        primary={t("mainDrawer.listItems.toDoList")}
        icon={<BorderColorIcon />}
        tooltip={collapsed}

      />
            <ListItemLink
                          tooltip={collapsed}
              to="/quick-messages"
              primary={t("mainDrawer.listItems.quickMessages")}
              icon={<FlashOnIcon />}
            />
            <ListItemLink
              to="/contacts"
              primary={t("mainDrawer.listItems.contacts")}
              icon={<ContactPhoneOutlinedIcon />}
              tooltip={collapsed}

            />
            <ListItemLink
              to="/schedules"
              primary={t("mainDrawer.listItems.schedules")}
              icon={<EventIcon />}
              tooltip={collapsed}

            />
            <ListItemLink
              to="/tags"
              primary={t("mainDrawer.listItems.tags")}
              icon={<LocalOfferIcon />}
              tooltip={collapsed}
            />
            <ListItemLink 
className={classes.formField}
                
                to="/files"
                primary={t("mainDrawer.listItems.files")}
                icon={<ListIcon />}
                tooltip={collapsed}

              />
              <ListItemLink 
className={classes.formField}
              to="/rankings"
              primary={t("mainDrawer.listItems.about")}
              icon={<EmojiEventsIcon />}
              tooltip={collapsed}

            />
          </>
        </>
        )}
      />

      <Can
        role={user.profile}
        perform={"drawer-admin-items:view"}
        yes={()=>(
          <>
            <Divider/>
            <ListSubheader 
            hidden={!drawerOpen}
            style={{
              position:"relative",
              fontSize: "14px",
              fontWeight: 600,
              textAlign: "right",
              padding: "5px 14px",
              margin: "8px 12px 4px 12px",
              color: isLightMode ? "#111827" : "#e8efd8",
              border: "1px solid rgba(46,227,122,0.35)",
              backgroundColor: "rgba(46,227,122,0.08)",
              borderRadius: "8px",
              boxShadow: "0 1px 4px rgba(46,227,122,0.10)",
              lineHeight: "1.5",
            }} 
            inset
            color="inherit">
              {t("mainDrawer.listItems.management")}
            </ListSubheader>
            <ListItemLink
            small
            to="/"
            primary={t("mainDrawer.listItems.dashboard")}
              icon={<PieChartIcon />}
              tooltip={collapsed}

            />
            {/* One-Experience consolidation (Slice-8 plan, staging wiring 22.07):
                the scattered AI surfaces fold into the AI-OS shell — ONE entry.
                Classic screens stay routable and are reachable from inside the
                shell's quick-access group. Hidden in native (Apple 2.1). */}
            {/* NO `|| "fallback"` ON t(), HERE OR ANYWHERE IN THIS FILE.
                i18next returns the KEY when a string is missing, and a key is a
                non-empty string, so `||` never sees anything falsy and the fallback
                after it can never run. Three call sites here carried one, which is
                why a missing entry surfaced as "mainDrawer.listItems.aiOs" in the
                rail rather than as the English text sitting right beside it.
                A safety net that catches nothing is worse than none: it reads as
                protection and stops anyone adding the key.
                If a label is missing, add it to all five dictionaries. */}
            {!isNativeApp() && (
              <ListItemLink
                to="/ai-os"
                dataTour="menu-ai-os"
                primary={t("mainDrawer.listItems.aiOs")}
                icon={<EmojiObjectsIcon />}
                tooltip={collapsed}
              />
            )}
{/* Disabled. Its `|| "Pipelines"` fallback is removed for the reason above, and
    if this is ever re-enabled note that `pipelines` exists in the en dictionary
    ONLY — he, ru, ar and es would render the raw key.

            <ListItemLink
              to="/pipelines"
              primary={t("mainDrawer.listItems.pipelines")}
              icon={<SendIcon />}
              tooltip={collapsed}
            />
*/}
            {String(localStorage.getItem("companyId")) === "1" && (
              <ListItemLink
                to="/offers"
                primary={t("mainDrawer.listItems.offers")}
                icon={<CardGiftcardIcon />}
                tooltip={collapsed}
              />
            )}
        </>
        )}
      />
      <Can
        role={user.profile}
        perform="drawer-admin-items:view"
        yes={() => (
          <>
            <Divider />
            <ListSubheader 
            hidden={!drawerOpen}
            style={{
              position:"relative",
              fontSize: "14px",
              fontWeight: 600,
              textAlign: "right",
              padding: "5px 14px",
              margin: "8px 12px 4px 12px",
              color: isLightMode ? "#111827" : "#e8efd8",
              border: "1px solid rgba(46,227,122,0.35)",
              backgroundColor: "rgba(46,227,122,0.08)",
              borderRadius: "8px",
              boxShadow: "0 1px 4px rgba(46,227,122,0.10)",
              lineHeight: "1.5",
            }} 
            inset
            color="inherit">
              {t("mainDrawer.listItems.administration")}
            </ListSubheader>
            
            {user.super && (
              <ListItemLink
                to="/announcements"
                primary={t("mainDrawer.listItems.annoucements")}
                icon={<AnnouncementIcon />}
                tooltip={collapsed}

              />
            )}
            <ListItemLink
              to="/campaigns"
              primary={t("mainDrawer.listItems.campaigns")}
              icon={<CampaignIcon />}
              tooltip={collapsed}
            />
            <ListItemLink
              to="/connections"
              dataTour="menu-connections"
              primary={t("mainDrawer.listItems.connections")}
              icon={
                <Badge badgeContent={connectionWarning ? "!" : 0} color="error">
                  <SyncAltIcon />
                </Badge>
              }
              tooltip={collapsed}

            />
            {/* WhatsApp Backups also lives as the "Archive" tab inside
                Settings — keeping both entries duplicated the same page
                in two places. Sidebar entry removed; the Archive tab in
                Settings is the canonical surface. Route is kept so old
                bookmarks/deep links don't 404. */}
            <ListItemLink
              to="/queues"
              dataTour="menu-queues"
              primary={t("mainDrawer.listItems.queues")}
              icon={<AccountTreeOutlinedIcon />}
              tooltip={collapsed}

            />
            <ListItemLink
              to="/users"
              dataTour="menu-users"
              primary={t("mainDrawer.listItems.users")}
              icon={<PeopleAltOutlinedIcon />}
              tooltip={collapsed}
            />
            <ListItemLink
              to="/teams"
              primary={t("mainDrawer.listItems.teams")}
              icon={<GroupIcon />}
              tooltip={collapsed}
            />

            {/* Apple App Store Guideline 3.1.1: hide the Billing menu entry
                inside the native iOS app. The website (whapiz.com) is where
                the company owner manages subscriptions. */}
            {!isNativeApp() && (
              <ListItemLink
                to="/billing"
                primary={t("mainDrawer.listItems.billing")}
                icon={<PaymentIcon />}
                tooltip={collapsed}
              />
            )}
            <ListItemLink
              to="/settings"
              primary={t("mainDrawer.listItems.settings")}
              icon={<SettingsOutlinedIcon />}
              tooltip={collapsed}
            />
          
          </>
        )}
      />
      <Divider />
    </div>
  );
};

export default MainListItems;